import { Component, OnInit, ChangeDetectionStrategy, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EstudioService } from '../../estudio.service';
import { Observable, of, combineLatest, from, BehaviorSubject, Subscription } from 'rxjs';
import { shareReplay, tap, map, startWith, concatMap, flatMap, toArray, mergeMap, catchError, timeout, distinctUntilChanged, delay } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { RetroalimentacionService } from '../_services/retroalimentacion.service';
import { ComentariosService } from 'src/app/_components/glm-comentarios/comentarios.service';
import { Comentario } from 'src/app/_models/comentario';
import { ClassroomService } from 'src/app/_services/classroom.service';

@Component({
  selector: 'app-retroalimentacion-trabajo-alumno',
  standalone: false,
  templateUrl: './retroalimentacion-trabajo-alumno.component.html',
  styleUrls: ['../retroalimentacion.component.scss','retroalimentacion-trabajo-alumno.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RetroalimentacionTrabajoAlumnoComponent implements OnInit, OnDestroy {

  evento$: Observable<any>;
  alumnoId: number;

  @Input()
  alumnos: any[];
  @Input()
  alumnos$: Observable<any[]>;
  @Input()
  eventoUnidadId$: number;
  @Input()
  columnas: any[];
  @Input()
  fechasEvento$: Observable<any>;
  @Input()
  fechaActual$: Observable<string>;
  @Input()
  criterios: any[];
  @Input()
  alumnoForm: UntypedFormControl;
  @Input()
  fechaForm: UntypedFormControl;
  @Input()
  mostrarAutoevaluacionForm: UntypedFormControl;
  @Input()
  mostrarAutoevaluacion$: Observable<boolean>;
  @Output()
  classroomActionEvent: EventEmitter<any>;
  @Output()
  navegarBackEvent: EventEmitter<any>;

  alumno$: Observable<any>;
  comentarioData$: Observable<{comentarios: Comentario[], targetId: number}>;
  archivoForm = new UntypedFormControl('');

  submission$: Observable<any>;
  archivos$: Observable<any>;

  private loadingSubject = new BehaviorSubject(true);
  loading$ = this.loadingSubject.asObservable();

  private expandidoSubject = new BehaviorSubject({state: false});
  expandido$ = this.expandidoSubject.asObservable();

  iframeSource$: Observable<any>;

  iframeSource;

  archivoSubscription: Subscription;

  constructor(
    private sanitizer: DomSanitizer,
    private retroalimentacionService: RetroalimentacionService,
    private comentariosService: ComentariosService,
  ) {
    this.classroomActionEvent = new EventEmitter();
    this.navegarBackEvent = new EventEmitter();
  }

  ngOnInit(): void {
    // Este observable es necesario para que la detección de cambios funcione.
    this.iframeSource$ = this.archivoForm.valueChanges.pipe(map(data => this.url(data)), startWith(this.url(this.archivoForm.value)));
    // Acá se define el source de del iframe con url saneada.
    this.archivoSubscription = this.archivoForm.valueChanges.pipe(distinctUntilChanged()).subscribe(res => {
      this.iframeSource = this.url(res);
    });
     this.alumno$ = combineLatest([this.alumnoForm.valueChanges.pipe(startWith(this.alumnoForm.value),
      // Se quita el src de el iframe para evitar memory leak.
      tap(() => this.archivoForm.setValue('about:blank')),
      delay(100),
      tap(() => this.loadingSubject.next(false)),
      tap(() => {
        setTimeout(() => {
          this.loadingSubject.next(true);
        }, 100);
      }),
      ), this.alumnos$]).pipe(
      map(([data, alumnos]) => {
        return alumnos.find(item => +item.id === data);
      }),
      shareReplay(1)
    );

    this.comentarioData$ = this.alumno$.pipe(
      // Se pide al api el identificador de eventoMateriaAlumnoId si el objeto no lo tiene.
      mergeMap(alumno => {
        if (alumno.eventoMateriaAlumnoId === 0 || alumno.eventoMateriaAlumnoId === undefined) {
          return this.retroalimentacionService.createEvaluation(alumno.unidadEventoMateriaId, alumno.id).pipe(
            map (data => {
              alumno.eventoMateriaAlumnoId = +data;
              return alumno;
            }),
            catchError(err => {
              return alumno;
            })
          );
        } else {
          return of(alumno);
        }
      }),
      // Se hace el llamado de comentarios con identificador de eventoMateriaAlumnoId
      concatMap(alumno => {
        return combineLatest(([
          this.comentariosService.getComentariosPorObjeto(alumno.eventoMateriaAlumnoId, 'unidadeventoalumno'),
          this.comentariosService.getComentariosPorObjeto(alumno.eventoMateriaAlumnoId, 'UnidadEventoAlumnoAutoevaluacion')
        ])).pipe(
          map(([evento, autoevaluacion]) => {
            return evento.concat(autoevaluacion);
          })
        ).pipe(
          catchError(err => {
            return [];
          }),
          map(data => {
            return {
              comentarios: data || [],
              targetId: alumno.eventoMateriaAlumnoId,
            };
          })
        );
      }),
    );

    this.archivos$ = this.alumno$.pipe(
      map(alumno => {
        alumno.urls = [];
        if (alumno.submission && alumno.submission.hasOwnProperty('assignmentSubmission') &&
          alumno.submission.assignmentSubmission.attachments) {
          alumno.submission.assignmentSubmission.attachments.forEach(element => {
            for (const prop in element) {
              if (element.hasOwnProperty(prop) && element[prop] !== null) {
                const url = element[prop];
                const regexp = new RegExp('^.*\.(jpg|JPG|pdf|PDF|jp2|JP2|j2k|J2K|jpf|JPF|jpx|JPX|jpm|JPM|mj2|MJ2|heic|HEIC|jpeg|JPEG|png|PNG|gif|GIF|bmp|BMP|dib|DIB|tif|TIFF|eps|EPS)$');
                const test = regexp.test(url.title);
                if (test) {
                  url.alternateLink = 'https://drive.google.com/file/d/' + url.id + '/preview';
                }
                alumno.urls.push(url);
              }
            }
          });
        }
        return alumno.urls;
      }),
      tap(data => {
        if (data.length > 0) {
          this.archivoForm.setValue(data[0].alternateLink);
        }
      }),
    );

  }

  url(urlParam: string) {
    return this.sanitizer.bypassSecurityTrustResourceUrl(urlParam);
  }

  indexChange(direction: number) {
    const index = this.alumnos.findIndex(item => +item.id === this.alumnoForm.value);
    this.alumnoForm.setValue(
      this.alumnos[index + direction < 0 ? 0 : (index + direction < this.alumnos.length ? index + direction : index)].id);
  }

  classroomAction(alumno: any, accion: string) {
    const alumnos = [alumno.id];
    this.classroomActionEvent.emit({alumnos, accion});
  }

  navegarAtras() {
    this.navegarBackEvent.emit();
  }

  expandir(estado: boolean) {
    this.expandidoSubject.next({state: estado});
  }

  ngOnDestroy(): void {
    if (this.archivoSubscription) {
      this.archivoSubscription.unsubscribe();
    }
  }
}
